/**
 * Weidner Via6 ContactForm — fork of the rise base.
 *
 * Differs from the base in four ways:
 *
 *  - No parallax background image. The form sits on a solid theme
 *    primary background; the `contactform.bg_image` slot group is
 *    omitted from the schema since nothing renders it anymore.
 *
 *  - A standalone section title is rendered above the form card
 *    (the editorial "cursive accent" pattern shared with
 *    `NeighborhoodTour` / `InteriorIntro` / `FloorplansList`). The
 *    `multihub-lead-form` block's own `form_header_text` heading is
 *    hidden via CSS so the label isn't duplicated inside the card.
 *
 *  - The form card carries a thick on-brand frame derived from
 *    `property_primary_color` via the same `lighten()` rule the
 *    `NeighborhoodMap` accordion uses to lift its surface off the
 *    surrounding primary section.
 *
 *  - The horizontal "info row" beneath the form is a custom
 *    three-card layout (Hours / Phone / Address), each with an
 *    outlined circular icon and an outlined pill button
 *    ("Give Us a Call" → `tel:`, "Text Us" → `sms:`, "Directions" →
 *    address URL). The shared `PropertyInfoPanel` from theme-rise
 *    renders a different vertical label-and-value layout — this
 *    component inlines its own rendering and only reuses the
 *    helper functions (phone formatting + office-hours condensing)
 *    so the editorial CTA-style cards can sit on the primary
 *    background with white-on-dark text. Schema tokens for the
 *    button labels and the middle card's descriptive blurb live in
 *    `contentSchema` below.
 */
import React, { useEffect, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  useCollectionBinding,
  useSlotGroup,
  usePropertyAddressUrl,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
  useProperty,
} from '@vibesites/runtime/react';
import { isClosedDay, withClosedDays, type OfficeHourEntry } from '../utils/officeHours';

// ============================================================================
// Color helpers (mirrors NeighborhoodMap.tsx — kept local because the
// helpers are not exported from that module). Used to derive the form's
// thick border from `property_primary_color` so the border reads as
// on-brand-but-lighter on every property.
// ============================================================================

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return { h: 200, s: 50, l: 50 };
  const r = parseInt(m[1], 16) / 255;
  const g = parseInt(m[2], 16) / 255;
  const b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function hueToHex(hue: number, sat: number, light: number): string {
  const h = hue / 360, s = sat / 100, l = light / 100;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * Bump a `#rrggbb` color's HSL lightness by `amount` percentage points
 * while preserving hue and saturation. Used here only for the form
 * card's thick frame (+8) — the info-row cards are borderless and the
 * pills use a flat white border, so neither needs a derived tone.
 */
function lighten(hex: string, amount: number): string {
  const { h, s, l } = hexToHsl(hex);
  const nextL = Math.min(100, Math.max(0, l + amount));
  return hueToHex(h, s, nextL);
}

// ============================================================================
// Phone / office-hours helpers (mirrors theme-rise/PropertyInfoPanel — kept
// local because that module's helpers are not exported and we render our
// own three-card layout below instead of delegating to it).
// ============================================================================

function formatDisplayPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;
  const [h, m] = trimmed.split(':').map(Number);
  if (isNaN(h)) return trimmed;
  const suffix = h >= 12 ? 'pm' : 'am';
  const hr = h % 12 || 12;
  return m ? `${hr}:${String(m).padStart(2, '0')} ${suffix}` : `${hr} ${suffix}`;
}

interface CondensedHourRow {
  dayLabel: string;
  timeLabel: string;
}

/** Collapse consecutive days that share open/close times — e.g. Mon-Fri: 10am-6pm. */
function condenseOfficeHours(hours: OfficeHourEntry[]): CondensedHourRow[] {
  // Pad omitted weekdays as Closed so Sat/Sun still appear in the hours card.
  const filled = withClosedDays(hours);
  const rows: CondensedHourRow[] = [];
  let i = 0;
  while (i < filled.length) {
    const cur = filled[i];
    const timeKey = isClosedDay(cur) ? 'closed' : `${cur.open_time ?? ''}|${cur.close_time ?? ''}`;
    let j = i + 1;
    while (j < filled.length) {
      const nxt = filled[j];
      const nxtKey = isClosedDay(nxt) ? 'closed' : `${nxt.open_time ?? ''}|${nxt.close_time ?? ''}`;
      if (nxtKey !== timeKey) break;
      j++;
    }
    const span = j - i;
    const dayLabel =
      span === 1
        ? (cur.day ?? '')
        : `${cur.day ?? ''} - ${filled[j - 1].day ?? ''}`;
    const timeLabel = isClosedDay(cur)
      ? 'Closed'
      : `${formatTime(cur.open_time)} - ${formatTime(cur.close_time)}`;
    rows.push({ dayLabel, timeLabel });
    i = j;
  }
  return rows;
}

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

// Address slot group — mirrors the Footer's address pattern so the
// directions card becomes a link to the property's Google Place page
// when set. The schema literal references this constant (extractor
// follows one level).
const addressLinkDef = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, City, ST 00000',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at
    // render time. Editor overrides still win.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// ============================================================================
// Component
// ============================================================================

export function ContactForm() {
  const prefix = 'contactform';
  const formBlockPath = `${prefix}.form_block`;
  const titlePath = `${prefix}.title`;
  const infoTitlePath = `${prefix}.info_title`;
  const blurbPath = `${prefix}.contact_blurb`;
  const phonePath = `${prefix}.phone`;
  const addressPath = `${prefix}.address`;
  const officeHoursPath = `${prefix}.office_hours`;
  const callBtnPath = `${prefix}.call_button_label`;
  const textBtnPath = `${prefix}.text_button_label`;
  const textUrlPath = `${prefix}.text_url`;
  const directionsBtnPath = `${prefix}.directions_button_label`;

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: 'multihub-lead-form',
  });

  // Lazy-load the "Satisfy" cursive Google Font used by
  // .vibe-cursive-accent in the title. InteriorIntro (which also
  // appears on the contact page) already lazy-loads this font and
  // marks the link with data-vibe-font="satisfy"; the dedupe guard
  // below early-returns when that marker is present, so loading
  // twice is a no-op. ContactForm still requests it independently
  // so the cursive accent renders correctly even if the contact
  // page is later rearranged without InteriorIntro.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="satisfy"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Satisfy&display=swap';
    link.setAttribute('data-vibe-font', 'satisfy');
    document.head.appendChild(link);
  }, []);

  // Derive an on-brand-but-lighter shade from the property primary
  // for the form-card frame (+8). Same lift used by `NeighborhoodMap`
  // for its accordion. The info-row cards / icons / pills below are
  // borderless or pure-white, so they don't need a per-property tone.
  const property = useProperty();
  const primaryColor = (property?.property_primary_color as string) || '#0a1f2e';
  const borderColor = useMemo(() => lighten(primaryColor, 8), [primaryColor]);

  // Info-row data (replaces the shared PropertyInfoPanel — see header note).
  const address = useSlotGroup(addressPath, addressLinkDef);
  const propertyAddressUrl = usePropertyAddressUrl();
  const addressHref = address.url || propertyAddressUrl || '';
  const phone = useContentToken(phonePath) || '';
  const officeHours = useCollectionBinding<OfficeHourEntry>(officeHoursPath, {
    collection: 'office_hours',
  });
  const callBtnLabel = useContentToken(callBtnPath);
  const textBtnLabel = useContentToken(textBtnPath);
  const textUrlOverride = useContentToken(textUrlPath);
  const directionsBtnLabel = useContentToken(directionsBtnPath);

  const phoneDisplay = phone ? formatDisplayPhone(phone) : '';
  const hourRows = condenseOfficeHours(officeHours);
  // Compact digits for sms:/tel: hrefs — sms: in particular dislikes
  // formatted numbers on some platforms.
  const phoneDigits = phone ? phone.replace(/\D/g, '') : '';
  // Text Us href: the `text_url` token is bound to the per-property
  // `custom_text_us_url` field by default (Weidner provisions a
  // hosted text-us widget URL per property), and editor overrides
  // still win. Falls back to `sms:<digits>` from the property phone
  // when neither the custom field nor an editor override is set, so
  // properties without a widget keep a working SMS link. Empty
  // string when nothing is available so the link renders as '#'
  // below.
  const textHref = textUrlOverride || (phoneDigits ? `sms:${phoneDigits}` : '');

  return (
    <>
      {/* ── Section 1: Centered lead form on a solid primary background ── */}
      <div className="cf-form-section">
        <div className="cf-form-content">
          {/* Section title sits ABOVE the form card on the primary
              background, mirroring the editorial pattern used by
              InteriorIntro / NeighborhoodTour / FloorplansList where a
              richtext heading mixes the regular heading font with a
              single cursive-accent word (.vibe-cursive-accent, the
              "Satisfy" Google font in the brand secondary color).
              Default renders "Contact us now." with "Contact" in
              cursive; editors can rewrite either the prose or the
              accent target via the inline richtext editor. */}
          <RichTextContent
            path={titlePath}
            defaultValue={
              '<span class="vibe-cursive-accent">Contact</span> us now.'
            }
            as="h2"
            className="cf-form-title"
          />
          <div className="cf-form-wrap" style={{ borderColor }}>
            <EditableToken path={formBlockPath} as="div">
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : (
                <div className="cf-form-empty-card">
                  <p className="cf-form-empty">No form block selected</p>
                </div>
              )}
            </EditableToken>
          </div>
        </div>
      </div>

      {/* ── Section 2: Three info cards (Hours / Phone / Address) ──
          Sits directly on the primary brand background, mirroring the
          example image where each card has an outlined circular icon,
          a short content block, and a rounded pill CTA. */}
      <div className="cf-info-row-section">
        <div className="cf-info-row-container">
          {/* Left-aligned editorial section title above the three info
              cards. Same cursive-accent pattern as .cf-form-title — a
              richtext heading mixes the heading serif with a single
              cursive word (.vibe-cursive-accent → Satisfy in the
              brand secondary color). Editors can rewrite the prose or
              the accent target via the inline richtext editor. */}
          <RichTextContent
            path={infoTitlePath}
            defaultValue={
              'Swing by to <span class="vibe-cursive-accent">chat</span> in person.'
            }
            as="h2"
            className="cf-info-title"
          />
          <div className="cf-info-cards">

            {/* Card 1 — Hours + "Give Us a Call" */}
            <div className="cf-card" style={{ background: borderColor }}>
              <div className="cf-card-row">
                <div className="cf-card-icon">
                  {/* Clock — outlined, matches the example */}
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <polyline points="12 7 12 12 15 14" />
                  </svg>
                </div>
                <div className="cf-card-text">
                  {hourRows.length > 0 ? (
                    hourRows.map((row, i) => (
                      <div key={i} className="cf-hours-line">
                        {row.dayLabel}: {row.timeLabel}
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="cf-hours-line">Mon-Fri: 10:00 am - 6:00 pm</div>
                      <div className="cf-hours-line">Sat &amp; Sun: 10:00 am - 6:00 pm</div>
                    </>
                  )}
                </div>
              </div>
              <a
                href={phoneDigits ? `tel:${phoneDigits}` : '#'}
                className="cf-pill"
              >
                <EditableToken path={callBtnPath} as="span">
                  {callBtnLabel || 'Give Us a Call'}
                </EditableToken>
              </a>
            </div>

            {/* Card 2 — Friendly-staff blurb + "Text Us" */}
            <div className="cf-card" style={{ background: borderColor }}>
              <div className="cf-card-row">
                <div className="cf-card-icon">
                  {/* Phone handset — outlined */}
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 4h3l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v3a2 2 0 0 1-2 2A15 15 0 0 1 3 6a2 2 0 0 1 2-2z" />
                  </svg>
                </div>
                <div className="cf-card-text">
                  <RichTextContent
                    path={blurbPath}
                    defaultValue="Contact one of our friendly staff members to answer any questions."
                    as="div"
                    className="cf-blurb"
                  />
                </div>
              </div>
              <a
                href={textHref || '#'}
                className="cf-pill"
              >
                <EditableToken path={textBtnPath} as="span">
                  {textBtnLabel || 'Text Us'}
                </EditableToken>
              </a>
            </div>

            {/* Card 3 — Address + "Directions" */}
            <div className="cf-card" style={{ background: borderColor }}>
              <div className="cf-card-row">
                <div className="cf-card-icon">
                  {/* Map pin — outlined */}
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 22s-7-7.58-7-13a7 7 0 0 1 14 0c0 5.42-7 13-7 13z" />
                    <circle cx="12" cy="9" r="2.5" />
                  </svg>
                </div>
                <div className="cf-card-text">
                  <EditableSlotGroup groupPath={addressPath} definition={addressLinkDef}>
                    <div className="cf-address">
                      {address.text || '123 Main Street, City, ST 00000'}
                    </div>
                  </EditableSlotGroup>
                </div>
              </div>
              <a
                href={addressHref || '#'}
                target={address._target || '_blank'}
                rel="noopener noreferrer"
                className="cf-pill"
              >
                <EditableToken path={directionsBtnPath} as="span">
                  {directionsBtnLabel || 'Directions'}
                </EditableToken>
              </a>
            </div>

          </div>
        </div>
      </div>

      {/* Schema-lint anchors — every schema key must have a matching
          wrapper somewhere in the tree. The visible wrappers above
          cover the title, form_block, contact_blurb, address slot
          group, and the three button labels. `phone` and `text_url`
          are only read (used for the tel:/sms:/custom href, not
          rendered as text), so each needs a hidden EditableToken
          anchor here. office_hours is a collection binding — no
          wrapper required. */}
      <EditableToken
        path={phonePath}
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      >
        {''}
      </EditableToken>
      <EditableToken
        path={textUrlPath}
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      >
        {''}
      </EditableToken>

      <style>{`
        /* ── Form section ── */
        .cf-form-section {
          position: relative;
          background: var(--vibe-color-primary);
          padding: 1.25rem 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        @media (min-width: 768px) {
          .cf-form-section {
            min-height: 520px;
            padding: 6rem 2rem;
          }
        }

        .cf-form-content {
          position: relative;
          z-index: 10;
          width: 100%;
          /* Stack title ABOVE the form card. Without an explicit
             flex-direction:column the title and the form-wrap would
             render side-by-side as flex-row siblings, which puts the
             title beside the form instead of above it. */
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .cf-form-wrap {
          width: 100%;
          max-width: 640px;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
          /* Thick on-brand frame. Color is derived per-property via
             lighten(primaryColor, 8) and applied inline above; this
             rule only owns the width/style so the border thickness is
             stable across properties. 60px desktop / 42px mobile is
             deliberately oversized — the frame functions as a major
             chrome element framing the form, not just a thin
             accent. The smaller mobile value keeps the form usable on
             narrow viewports where 60px would crowd the inputs. */
          border: 42px solid var(--vibe-color-primary);
        }
        @media (min-width: 768px) {
          .cf-form-wrap {
            border-width: 60px;
          }
        }
        /* Hide the multihub-lead-form block's internal heading
           (the form_header_text token, rendered as the only h3
           inside the form card). The standalone .cf-form-title
           h2 above the card replaces it, so showing both would
           duplicate the label. Scoped to .cf-form-wrap so it
           does not reach into any other block someone might swap
           in later that also uses h3s for legitimate structural
           reasons. */
        .cf-form-wrap h3 {
          display: none !important;
        }

        /* Section title above the form card. Mirrors NeighborhoodMap's
           "Explore the Neighborhoods" h2 — sits on the primary
           background in white-on-primary, heading serif, with
           generous spacing below so it reads as a label for the form
           card beneath it. */
        .cf-form-title {
          font-family: var(--vibe-font-heading);
          /* Sized to sit alongside the page's other editorial
             section titles (NeighborhoodTour's "Discover downtown
             …", FloorplansList's "Comforts of home", etc.) which
             all land in the 1.875rem → 3rem range. */
          font-size: 2rem;
          line-height: 1.15;
          font-weight: 600;
          letter-spacing: 0.01em;
          color: var(--vibe-color-text-on-primary, #fff);
          width: 100%;
          max-width: 640px;
          text-align: left;
          margin: 0 0 1.5rem 0;
        }
        @media (min-width: 768px) {
          .cf-form-title {
            font-size: 3rem;
            margin-bottom: 2rem;
          }
        }
        .cf-form-title .vibe-cursive-accent {
          font-family: 'Satisfy', cursive;
          color: var(--vibe-color-secondary);
          font-weight: 400;
          text-transform: none;
          letter-spacing: 0;
        }
        .cf-form-empty-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }

        /* ── Info-row section ──
           Sits directly on the same primary background as the form,
           so the cards visually continue the dark theme. The cards
           themselves are not separate "white tiles" like the base
           PropertyInfoPanel — they're outlined containers on the same
           dark surface, matching the editorial example image. */
        .cf-info-row-section {
          background: var(--vibe-color-primary);
          padding: 0 1.5rem 3rem;
        }
        @media (min-width: 768px) {
          .cf-info-row-section {
            padding: 0 2rem 5rem;
          }
        }
        .cf-info-row-container {
          max-width: 72rem;
          margin: 0 auto;
        }

        /* Section title above the three info cards. Mirrors
           .cf-form-title — heading serif on the primary background in
           white, with a cursive-accent word (.vibe-cursive-accent →
           Satisfy in brand secondary). Left-aligned so it labels the
           card grid below without centering against the wider info
           row, matching the editorial side-of-page heading pattern. */
        .cf-info-title {
          font-family: var(--vibe-font-heading);
          font-size: 2rem;
          line-height: 1.15;
          font-weight: 600;
          letter-spacing: 0.01em;
          color: var(--vibe-color-text-on-primary, #fff);
          text-align: left;
          margin: 0 0 1.5rem 0;
        }
        @media (min-width: 768px) {
          .cf-info-title {
            font-size: 3rem;
            margin-bottom: 2rem;
          }
        }
        .cf-info-title .vibe-cursive-accent {
          font-family: 'Satisfy', cursive;
          color: var(--vibe-color-secondary);
          font-weight: 400;
          text-transform: none;
          letter-spacing: 0;
        }

        .cf-info-cards {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
        }
        @media (min-width: 768px) {
          .cf-info-cards {
            grid-template-columns: repeat(3, 1fr);
            gap: 1.5rem;
          }
        }

        /* ── Card ──
           Borderless tile filled with the same lifted on-brand tone
           (lighten(primaryColor, 8)) that the form-wrap above uses
           for its thick frame, so the three info tiles read as a
           continuation of the form chrome rather than floating
           islands on the primary background. The fill is applied
           inline from React (per-property derived) — this rule sets
           a transparent fallback so the cards degrade gracefully if
           the inline style is ever dropped. */
        .cf-card {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: 1.25rem;
          padding: 1.5rem;
          border: 0;
          border-radius: 0;
          background: transparent;
          color: var(--vibe-color-text-on-primary, #fff);
        }
        @media (min-width: 768px) {
          .cf-card {
            padding: 1.75rem;
            gap: 1.5rem;
          }
        }

        /* Top row of each card: icon on the left, content on the right.
           A flex row (not grid) so the icon stays at its intrinsic
           size while the text fills the remaining width. Items are
           centered vertically so the icon glyph lines up with the
           middle of the adjacent text block — the icon box is taller
           than most of the single-line content (the hours card has
           two lines, the blurb/address are one line), so top-aligning
           leaves the glyph floating above the first line. */
        .cf-card-row {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        /* Borderless icon — the stroked SVG glyph sits directly on
           the primary background with no surrounding circle. Sized
           large enough (72px box / 40px glyph mobile, 88px / 48px
           desktop) to read as a section marker without the outlined
           ring; the wider box is a touch target / alignment guide,
           the glyph is what's actually drawn. */
        .cf-card-icon {
          flex-shrink: 0;
          width: 72px;
          height: 72px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 0;
          background: transparent;
          color: var(--vibe-color-text-on-primary, #fff);
        }
        .cf-card-icon svg {
          width: 40px;
          height: 40px;
          display: block;
        }
        @media (min-width: 768px) {
          .cf-card-icon {
            width: 88px;
            height: 88px;
          }
          .cf-card-icon svg {
            width: 48px;
            height: 48px;
          }
        }

        /* Card text column. Inherits the white-on-primary color from
           .cf-card; the size+leading sit just below the heading scale
           so the cards read as secondary chrome under the form. */
        .cf-card-text {
          flex: 1 1 auto;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.45;
          color: var(--vibe-color-text-on-primary, #fff);
        }
        .cf-hours-line {
          margin: 0;
        }
        .cf-hours-line + .cf-hours-line {
          margin-top: 0.2rem;
        }
        .cf-blurb {
          margin: 0;
        }
        .cf-blurb p {
          margin: 0;
        }
        .cf-address {
          margin: 0;
        }

        /* Rounded pill CTA. Border is solid white (not the lifted
           on-brand outline used elsewhere) so the buttons read as
           the most chromed element in the card and don't shift hue
           per property primary. Width auto-fills the card so all
           three pills share the same horizontal footprint across
           columns. Font is body (sans), NOT heading (Prata) — the
           CTA label is functional UI, not editorial typography. */
        .cf-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          align-self: stretch;
          padding: 0.85rem 1.5rem;
          border: 1.5px solid #fff;
          border-radius: 999px;
          background: transparent;
          color: var(--vibe-color-text-on-primary, #fff);
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          text-decoration: none;
          transition: background-color 0.2s ease, color 0.2s ease;
        }
        .cf-pill:hover,
        .cf-pill:focus-visible {
          background: #fff;
          color: var(--vibe-color-primary);
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.title': {
      label: 'Section Title',
      type: 'richtext',
      defaultValue: '<span class="vibe-cursive-accent">Contact</span> us now.',
      description:
        'Heading rendered above the form card. Wrap a word in <span class="vibe-cursive-accent">…</span> to render it in the Satisfy cursive script (brand secondary color).',
    },
    'contactform.info_title': {
      label: 'Info Cards Title',
      type: 'richtext',
      defaultValue:
        'Swing by to <span class="vibe-cursive-accent">chat</span> in person.',
      description:
        'Left-aligned heading rendered above the three contact info cards. Wrap a word in <span class="vibe-cursive-accent">…</span> to render it in the Satisfy cursive script (brand secondary color).',
    },
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactform.contact_blurb': {
      label: 'Contact Card Blurb',
      type: 'richtext',
      defaultValue: 'Contact one of our friendly staff members to answer any questions.',
      description:
        'Short descriptive line shown in the middle info card, above the Text Us button.',
    },
    'contactform.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'contactform.call_button_label': {
      label: 'Call Button Label',
      type: 'text',
      defaultValue: 'Give Us a Call',
    },
    'contactform.text_button_label': {
      label: 'Text Button Label',
      type: 'text',
      defaultValue: 'Text Us',
    },
    'contactform.text_url': {
      label: 'Text Us URL',
      type: 'url',
      // Bind to the per-property custom field `custom_text_us_url`
      // (Weidner provisions a hosted text-us widget URL on each
      // property). When the property does not set the field, the
      // component falls back to `sms:<property phone>` at render
      // time. Editor overrides on this token still win.
      defaultField: 'custom_text_us_url',
      suggestedFields: ['custom_text_us_url'],
      defaultValue: '',
      description:
        'URL for the Text Us button. Defaults to the property `custom_text_us_url` field; when empty, the button falls back to sms:<property phone>.',
    },
    'contactform.directions_button_label': {
      label: 'Directions Button Label',
      type: 'text',
      defaultValue: 'Directions',
    },
  },
  slotGroups: {
    'contactform.address': addressLinkDef,
  },
  collectionBindings: {
    'contactform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
